import {
  ArrowLeft,
  ArrowRight,
  Bluetooth,
  Camera,
  CircleAlert,
  LockKeyhole,
} from 'lucide-react';

export function ArticlePage() {
  return (
    <>
      <main id="content" className="article-main" tabIndex={-1}>
        <nav
          className="article-breadcrumb shell"
          aria-label="Article navigation"
        >
          <a href="/spoolmark/blog">
            <ArrowLeft size={15} aria-hidden="true" /> All articles
          </a>
          <span>Field note 01 · 2 min read</span>
        </nav>

        <article>
          <header className="article-header shell">
            <p className="eyebrow">Field note 01 · iOS / NFC</p>
            <h1>How I Made Bambu Lab Spool Scanning Work on iPhone</h1>
            <p className="article-dek">
              Two practical ways around a hard platform limit: quick camera
              intake, or an exact read with optional Bluetooth hardware.
            </p>
            <div className="article-byline">
              <span className="author-mark" aria-hidden="true">
                AN
              </span>
              <p>
                <strong>Alex Nosov</strong>
                <span>Maker of Spoolmark</span>
              </p>
            </div>
            <div
              className="article-facts"
              aria-label="Article verification details"
            >
              <span>
                Updated · <time dateTime="2026-09-18">Sep 18, 2026</time>
              </span>
              <span>2 min read</span>
              <span>iPhone development preview</span>
            </div>
          </header>

          <section
            className="article-paths"
            aria-labelledby="article-paths-title"
          >
            <div className="shell">
              <div className="article-paths-heading">
                <div>
                  <p className="eyebrow">Two preview workflows</p>
                  <h2 id="article-paths-title">Choose what matters most.</h2>
                </div>
                <p>
                  Quick intake with the camera, or exact tag data with optional
                  hardware.
                </p>
              </div>
              <div className="article-path-grid">
                <article>
                  <div>
                    <Camera size={22} aria-hidden="true" />
                    <span>01 / Camera</span>
                  </div>
                  <h3>Fast intake</h3>
                  <p>
                    Any labeled spool. No extra hardware. Review before save.
                  </p>
                </article>
                <article>
                  <div>
                    <Bluetooth size={22} aria-hidden="true" />
                    <span>02 / Reader</span>
                  </div>
                  <h3>Exact tag data</h3>
                  <p>
                    Original Bambu tag data through an optional read-only
                    reader.
                  </p>
                </article>
              </div>
            </div>
          </section>

          <div className="article-layout shell">
            <div className="article-body">
              <p>
                When I started bringing Spoolmark to iOS, I ran into an annoying
                problem: an iPhone cannot read the original Bambu Lab RFID tags
                directly.
              </p>

              <p>
                Those tags use MIFARE Classic. Some Android phones support it,
                but Apple does not provide the access needed through Core NFC.
                There is no extra permission or clever NFC library that can
                unlock it.
              </p>

              <p>
                I still wanted iPhone users to avoid typing every spool detail
                by hand, so I added two options to the development build.
              </p>

              <div className="article-status-note">
                <strong>Availability</strong>
                <p>
                  This article describes the iPhone development build. A public
                  App Store release is not available yet. Camera and NFC
                  workflows still need physical-device qualification; the ACS
                  reader integration also needs redistribution review. Final
                  supported features will be confirmed before release.
                </p>
              </div>

              <h2>The easy option: take two photos</h2>

              <p>
                If you do not have an external reader, tap{' '}
                <strong>Use camera</strong> in the app. Spoolmark guides you
                through two quick photos:
              </p>

              <ol>
                <li>
                  Take a photo of the whole spool with the label visible. The
                  app reads the printed text and suggests details such as the
                  brand, material, and product.
                </li>
                <li>
                  Take a close-up of the filament. The app looks at the visible
                  color and suggests a color name.
                </li>
              </ol>

              <p>
                You can review and correct everything before adding the spool to
                your inventory. The photos are analyzed on the iPhone and
                discarded afterward.
              </p>

              <div className="article-callout">
                <CircleAlert size={22} aria-hidden="true" />
                <p>
                  This is not an RFID scan, so it cannot read the tag&apos;s
                  unique ID, exact spool identity, or how much filament is left.
                  Lighting, glare, and silk, translucent, or multicolor filament
                  can also affect the color result.
                </p>
              </div>

              <p>
                Still, it is a quick option that needs no extra hardware—and it
                works with many labeled spools, not just Bambu Lab products.
              </p>

              <h2>The exact option: use a Bluetooth reader</h2>

              <p>
                If you want the data stored on the original Bambu Lab tag,
                the development build includes an integration for the{' '}
                <strong>ACS ACR1555U-A1</strong> Bluetooth NFC reader.
              </p>

              <p>
                Turn on the reader, tap <strong>Use ACR1555U</strong>, and place
                the spool&apos;s RFID tag on it. The reader handles the part
                that the iPhone cannot, while the app turns the result into
                useful details such as material, color, spool identity, full
                spool weight, and printing information.
              </p>

              <div className="article-callout article-callout-safe">
                <LockKeyhole size={22} aria-hidden="true" />
                <p>
                  The scan stays on your device and is read-only. Spoolmark does
                  not change or clone the Bambu Lab tag.
                </p>
              </div>

              <p>
                The development build targets iOS 15 or later. This workflow
                requires an ACR1555U-A1 reader, sold separately; confirmed
                device compatibility will be published after testing.
              </p>

              <h2>What each option is designed for</h2>

              <p>
                Camera intake is designed to add a spool quickly with only your
                iPhone. The Bluetooth reader integration is designed to read
                data directly from the original tag. Public availability of
                these workflows is still being prepared.
              </p>

              <p>
                Both options end up in the same local inventory and save you
                from entering every spool by hand.
              </p>

              <section
                className="article-sources"
                aria-labelledby="article-sources-title"
              >
                <h2 id="article-sources-title">
                  Platform and device references
                </h2>
                <ul>
                  <li>
                    <a
                      href="https://developer.apple.com/documentation/corenfc"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Apple Core NFC documentation
                    </a>
                    <span>Native NFC capabilities and tag interfaces.</span>
                  </li>
                  <li>
                    <a
                      href="https://www.acs.com.hk/en/download-manual/14205/PPE-ACR1555U-A1-1.05-EN.pdf"
                      target="_blank"
                      rel="noreferrer"
                    >
                      ACS ACR1555U-A1 product brief
                    </a>
                    <span>Reader hardware and supported NFC standards.</span>
                  </li>
                </ul>
              </section>

              <p className="article-disclaimer">
                Spoolmark is an independent app and is not affiliated with Bambu
                Lab or ACS.
              </p>
            </div>
          </div>
        </article>

        <section className="article-next shell">
          <p className="eyebrow">Keep exploring</p>
          <h2>The product behind the guide.</h2>
          <p>
            Spoolmark is building a trustworthy, local-first inventory for
            mixed-brand filament shelves.
          </p>
          <a className="button button-dark" href="/spoolmark">
            Explore Spoolmark <ArrowRight size={18} aria-hidden="true" />
          </a>
        </section>
      </main>
    </>
  );
}
